/**
 * CredentialStatusBadge — compact list-column status pill for dashboard-eligible
 * credentials.
 *
 * Exports:
 *   - `CredentialStatusBadgeControlled` — reads probe state from the shared hook
 *     (the hook lives in `useCredentialStatusProbe.ts` per fast-refresh rules)
 *
 * For workflow-only credentials (no `connectionStatus`) renders the §38.6 "—"
 * not-applicable placeholder — intentional absence, not broken/empty.
 *
 * Accessibility (§38.8):
 *   - Never color alone: icon + label + color on every state.
 *   - testing state wrapped in role="status" aria-live="polite".
 *   - Last tested relative time exposes absolute via title on hover.
 *   - "—" placeholder carries explanatory aria-label.
 *
 * Dashboard-era only. Brand guide §38. Slice 3.1 — credential-status Epic 3.
 */

import { CSSProperties } from 'react'
import {
  CheckCircle2,
  XCircle,
  ShieldAlert,
  CircleDashed,
  Loader2,
  HelpCircle,
} from 'lucide-react'
import type { CredentialConnectionStatus } from '@/types/credentials'
import {
  DisplayStatusState,
  STATUS_PRESENTATION,
  formatRelativeTime,
  formatAbsoluteTime,
} from './credentialStatus'

// ─── Icon map ─────────────────────────────────────────────────────────────────

type IconProps = { size: number; className?: string; 'aria-hidden': true }
type IconFC = (props: IconProps) => JSX.Element

const STATUS_ICON: Record<DisplayStatusState, IconFC> = {
  valid: (props) => <CheckCircle2 {...props} />,
  invalid_key: (props) => <XCircle {...props} />,
  missing_permissions: (props) => <ShieldAlert {...props} />,
  not_tested_yet: (props) => <CircleDashed {...props} />,
  testing: (props) => <Loader2 {...props} className={`${props.className ?? ''} animate-spin`.trim()} />,
  couldnt_verify: (props) => <HelpCircle {...props} />,
}

// ─── Controlled badge (reads shared probe state from parent) ─────────────────

export interface CredentialStatusBadgeControlledProps {
  credentialId: string
  displayState: DisplayStatusState
  displayLastTestedAt: string | null
  connectionStatus: CredentialConnectionStatus | undefined
}

export const CredentialStatusBadgeControlled = ({
  credentialId,
  displayState,
  displayLastTestedAt,
  connectionStatus,
}: CredentialStatusBadgeControlledProps) => {
  // §38.6 not-applicable placeholder for workflow-only credentials.
  if (connectionStatus === undefined) {
    return (
      <span
        className="text-[14px] font-medium text-muted-foreground"
        aria-label="Connection status not applicable for this credential"
      >
        —
      </span>
    )
  }

  const pres = STATUS_PRESENTATION[displayState]
  const IconComponent = STATUS_ICON[displayState]

  const pillStyle: CSSProperties = {
    color: pres.color,
    backgroundColor: pres.bgColor,
    borderColor: pres.borderColor,
  }

  const relativeTime = formatRelativeTime(displayLastTestedAt)
  const absoluteTime = formatAbsoluteTime(displayLastTestedAt)

  return (
    <div className="flex flex-col gap-1 items-start" data-testid={`status-cell-${credentialId}`}>
      <span
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold rounded-full px-2.5 py-1 border"
        style={pillStyle}
        role={displayState === 'testing' ? 'status' : undefined}
        aria-live={displayState === 'testing' ? 'polite' : undefined}
        data-testid={`status-badge-${credentialId}`}
      >
        <IconComponent size={14} aria-hidden={true} />
        {pres.label}
      </span>
      {relativeTime && displayState !== 'testing' && displayState !== 'not_tested_yet' && (
        <span
          className="text-xs text-muted-foreground border-b border-dotted border-border pb-px cursor-default"
          title={absoluteTime ?? undefined}
          data-testid={`status-meta-${credentialId}`}
        >
          Last tested {relativeTime}
        </span>
      )}
    </div>
  )
}
